import type { Tokens } from './oauth.js';

/**
 * Where the tokens live - AUTH-004.
 *
 * **In memory, and nowhere else.** A token in `localStorage` is readable by any
 * script that ever runs on this origin, survives the tab, and cannot be revoked
 * from here; the access token is fifteen minutes by design (`auth-stack.ts`)
 * precisely so that holding it briefly is the safe option.
 *
 * Two things do touch `sessionStorage`, and both are deliberate:
 *
 *  * the PKCE verifier and state, because they have to survive the redirect to
 *    Cognito and back. They are single-use, scoped to this tab, and deleted the
 *    moment the code is redeemed;
 *  * two flags, neither of them a credential: one saying a session existed in
 *    this tab, and one saying a silent re-entry has already been tried. They
 *    carry nothing, and between them they are what makes a reload keep the
 *    session - see `markSilentAttempt` for why the second one has to outlive
 *    the page.
 */

const PENDING_KEY = 'coworker.auth.pending';
const RETURNING_KEY = 'coworker.auth.returning';
const SILENT_KEY = 'coworker.auth.silent';

export interface PendingLogin {
  readonly verifier: string;
  readonly state: string;
  /** Where to land afterwards, so a deep link survives the round trip. */
  readonly returnTo: string;
}

/** Every access is guarded: Safari in private mode throws on `sessionStorage`. */
function safely<T>(read: () => T, fallback: T): T {
  try {
    return read();
  } catch {
    return fallback;
  }
}

export function rememberPending(pending: PendingLogin, storage = globalThis.sessionStorage): void {
  safely(() => storage.setItem(PENDING_KEY, JSON.stringify(pending)), undefined);
}

/** Reads and clears. A verifier is single-use; leaving it invites a replay. */
export function takePending(storage = globalThis.sessionStorage): PendingLogin | null {
  const raw = safely(() => storage.getItem(PENDING_KEY), null);
  safely(() => storage.removeItem(PENDING_KEY), undefined);
  if (raw === null) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<PendingLogin>;
    if (typeof parsed.verifier !== 'string' || typeof parsed.state !== 'string') return null;
    return {
      verifier: parsed.verifier,
      state: parsed.state,
      returnTo: typeof parsed.returnTo === 'string' ? parsed.returnTo : '/',
    };
  } catch {
    return null;
  }
}

export function markReturning(storage = globalThis.sessionStorage): void {
  safely(() => storage.setItem(RETURNING_KEY, '1'), undefined);
}

export function clearReturning(storage = globalThis.sessionStorage): void {
  safely(() => storage.removeItem(RETURNING_KEY), undefined);
}

/** True when this tab has signed in before, so a reload can re-enter silently. */
export function isReturning(storage = globalThis.sessionStorage): boolean {
  return safely(() => storage.getItem(RETURNING_KEY), null) === '1';
}

/**
 * One silent re-entry per tab, and the whole reason this is in `sessionStorage`
 * rather than a ref: the attempt *is* a page load, so anything held in memory
 * resets at exactly the moment it would have to be remembered.
 *
 * Set before leaving for Cognito, cleared only when a code actually redeems. So
 * a hosted-UI session that has expired, or a person who backs out of the login
 * form, comes back to our own sign-in screen and stays on it. Without the flag
 * that same person is thrown at Cognito again by the very next render, which is
 * a page they cannot leave.
 */
export function markSilentAttempt(storage = globalThis.sessionStorage): void {
  safely(() => storage.setItem(SILENT_KEY, '1'), undefined);
}

export function clearSilentAttempt(storage = globalThis.sessionStorage): void {
  safely(() => storage.removeItem(SILENT_KEY), undefined);
}

export function silentAttempted(storage = globalThis.sessionStorage): boolean {
  return safely(() => storage.getItem(SILENT_KEY), null) === '1';
}

/**
 * The live tokens. A plain object rather than React state: `ApiClient` reads the
 * access token through a function on every request, and a re-render is not what
 * should decide which token a request carries.
 */
export class TokenStore {
  #tokens: Tokens | null = null;

  set(tokens: Tokens): void {
    this.#tokens = tokens;
  }

  clear(): void {
    this.#tokens = null;
  }

  get tokens(): Tokens | null {
    return this.#tokens;
  }

  /** `null` once expired, so a caller never sends a token the gateway will refuse. */
  accessToken(now: number = Date.now()): string | null {
    if (this.#tokens === null || this.#tokens.expiresAt <= now) return null;
    return this.#tokens.accessToken;
  }

  /** AUTH-010 needs this one: an access token carries no email and no name. */
  idToken(): string | null {
    return this.#tokens?.idToken ?? null;
  }
}
